import Splide from '@splidejs/splide';
import '@splidejs/splide/dist/css/splide.min.css';

import Component from '../../components/component/component';
import Helpers, { debounce, DebouncedFunction } from '../../components/helpers/helpers';

import './card-carousel.scss';

export default class CardCarousel extends Component {
    static initSelector: string = '.js-card-carousel';

    carousel: Splide;
    component: string;
    container: HTMLElement;
    next: HTMLElement;
    prev: HTMLElement;
    debouncedResizeHandler: DebouncedFunction<() => void>;

    constructor(target: HTMLElement) {
        super(target);

        this.component = this.element.className.match(/component-([-a-z]+)/)[0].replace('component-', '');
        this.container = this.element.querySelector('.card-carousel__container');
        this.next = this.element.querySelector('.card-carousel__arrow--next');
        this.prev = this.element.querySelector('.card-carousel__arrow--prev');
        this.debouncedResizeHandler = debounce(this.resizeHandler.bind(this), 50);

        this.init();
    }

    init(): void {
        this.initSplide();

        this.checkArrows();

        this.next.addEventListener('click', this.onNextClick.bind(this));
        this.prev.addEventListener('click', this.onPrevClick.bind(this));

        this.setListeners();
    }

    initSplide(): void {
        this.element.querySelectorAll('.card-carousel__carousel').forEach((element: HTMLElement): void => {
            const listWidth: number = this.container.offsetWidth - parseInt(window.getComputedStyle(this.container).paddingLeft, 10) * 2;

            this.carousel = new Splide(element, {
                arrows: false,
                breakpoints: {
                    [Helpers.bp.lg]: {
                        fixedWidth: listWidth / this.getPerPage() * .8 + 'px',
                        gap: 12,
                        padding: window.getComputedStyle(this.container).paddingLeft,
                        perPage: this.getPerPage(),
                        width: '100vw',
                    },
                },
                drag: Helpers.isMobileDevice,
                fixedWidth: (listWidth + 24) / this.getPerPage() + 'px',
                gap: 0,
                pagination: false,
                perPage: this.getPerPage(),
                preloadPages: 3,
                speed: 600,
                type: 'slide',
                waitForTransition: true,
                width: listWidth,
            });

            this.carousel.on('mounted', () => {
                this.element.classList.add('is-mounted');
            });

            this.carousel.mount();
        });
    }

    setListeners(): void {
        window.addEventListener('resize', this.debouncedResizeHandler);
    }

    resizeHandler(): void {
        this.carousel.destroy(true);
        this.initSplide();
    }

    getPerPage(): number {
        if (window.matchMedia('(min-width: ' + Helpers.bp.lg + 'px)').matches) {
            const customPerPage: string | undefined = this.element.querySelector('.card-carousel__carousel').getAttribute('data-per-page');

            if (customPerPage !== 'undefined' && customPerPage !== null) {
                return parseInt(customPerPage);
            } else {
                switch (this.component) {
                    case 'movie-card':
                        return 3;
                    case 'campaign-card':
                        return 2;
                    case 'genre-card':
                        return 6;
                    default:
                        return 1;
                }
            }
        } else {
            switch (this.component) {
                case 'movie-card':
                    return 1;
                case 'campaign-card':
                    return 1;
                case 'genre-card':
                    return 2;
                default:
                    return 1;
            }
        }
    }

    checkArrows(): void {
        if (this.carousel.index + this.carousel.options.perPage >= this.carousel.length) {
            this.next.classList.add('is-disabled');
        } else {
            this.next.classList.remove('is-disabled');
        }

        if (this.carousel.index <= 0) {
            this.prev.classList.add('is-disabled');
        } else {
            this.prev.classList.remove('is-disabled');
        }
    }

    onNextClick(): void {
        this.carousel.go('>');
        this.checkArrows();
    }

    onPrevClick(): void {
        this.carousel.go('<');
        this.checkArrows();
    }
}
